import { cartRoutes, fetchCart, writeCart } from '@theme/eq-cart-write';
const CARD_SELECTOR = '.eq-product-card';
const QUANTITY_SELECTOR = '.eq-product-card__quantity';
const CART_ICON_SELECTOR = 'cart-icon';
const STEPPER_SELECTOR = '.eq-product-card__quantity-stepper';
const ACTIVE_CLASS = 'active';
const HIDDEN_CLASS = 'hidden';
const EXPANDED_CLASS = 'eq-product-card__quantity-stepper--expanded';
const isCompact = () => window.matchMedia('(max-width: 749px)').matches;
const expandStepper = (stepper) => {
  for (const other of document.querySelectorAll(`.${EXPANDED_CLASS}`)) {
    if (other !== stepper) other.classList.remove(EXPANDED_CLASS);
  }
  stepper?.classList.add(EXPANDED_CLASS);
};
// A control scoped to a line property (an offer page) only sees the lines that carry it, so the
// same variant bought elsewhere neither lights the card up nor gets its quantity edited.
const itemsFor = (cart, property) =>
  property ? cart.items.filter((item) => item.properties?.[property] === 'true') : cart.items;
const applyCart = (cart) => {
  for (const card of document.querySelectorAll(CARD_SELECTOR)) {
    if (!(card instanceof HTMLElement)) continue;
    const items = itemsFor(cart, card.querySelector(QUANTITY_SELECTOR)?.getAttribute('data-line-property'));
    const variantId = card.closest('[data-variant-id]')?.getAttribute('data-variant-id');
    const productId = card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
    const inCart = variantId
      ? items.some((item) => String(item.id) === variantId)
      : Boolean(productId) && items.some((item) => String(item.product_id) === String(productId));
    card.classList.toggle(ACTIVE_CLASS, inCart);
  }
  for (const control of document.querySelectorAll(QUANTITY_SELECTOR)) {
    if (!(control instanceof HTMLElement)) continue;
    const matching = itemsFor(cart, control.dataset.lineProperty)
      .filter((item) => String(item.id) === control.dataset.variantId);
    // A discount can split one variant across two cart lines — a free one and a paid one, with
    // identical properties (every-8th-oil-free) or differing ones (B20G10). The stepper counts both
    // and steps the paid line, so a press lands on the line the shopper can still pay for.
    //
    // Every grid gets this, not only quantity-discount offers, so a card's number is the variant's
    // whole cart total rather than one line's — the same reading as the `active` class, which marks
    // the card from any line. A control with no `line_property` sees every line for the variant,
    // including ones an offer page wrote.
    const paid = matching.filter((item) => item.final_line_price > 0);
    const candidates = paid.length ? paid : matching;
    // The tiebreak is the oldest line, not the newest, because that is the one the Liquid renders
    // into `data-line-key`. On an offer page every line carries `line_property`, so none is ever
    // property-less and the tiebreak decides on its own; picking the other end would hand the first
    // press one line and the press after the cart syncs another.
    const line = candidates.find((item) => Object.keys(item.properties || {}).length === 0) || candidates[0];
    const quantity = matching.reduce((sum, item) => sum + item.quantity, 0);
    control.dataset.lineQuantity = String(line?.quantity ?? 0);
    if (line?.key) control.dataset.lineKey = line.key;
    else delete control.dataset.lineKey;
    control.querySelector('.eq-product-card__quantity-add')?.classList.toggle(HIDDEN_CLASS, quantity > 0);
    const stepper = control.querySelector(STEPPER_SELECTOR);
    stepper?.classList.toggle(HIDDEN_CLASS, quantity === 0);
    if (quantity === 0) stepper?.classList.remove(EXPANDED_CLASS);
    control.querySelector('.eq-product-card__quantity-remove')?.classList.toggle(HIDDEN_CLASS, quantity > 1);
    control.querySelector('.eq-product-card__quantity-subtract')?.classList.toggle(HIDDEN_CLASS, quantity < 2);
    const input = control.querySelector('.eq-product-card__quantity-input');
    if (input instanceof HTMLInputElement) input.value = String(Math.max(quantity, 1));
  }
};
const syncFromCart = async () => {
  const cart = await fetchCart();
  if (cart) applyCart(cart);
};
// Which confirmation a card gives after a cart write is the grid's decision, carried on the
// control: `data-cart-drawer="open"` asks for the cart drawer, and on mobile the notification takes
// its place. The breakpoint has to stay in step with `eq-cart-notification.js`, or a width between
// the two would either show both or neither.
const mutateCart = async ({ action, url, body, variantId, quantity, control }) => {
  if (control.dataset.busy === 'true') return;
  control.dataset.busy = 'true';
  const wantsDrawer = control.dataset.cartDrawer === 'open' && !isCompact();
  try {
    const cart = await writeCart({
      url,
      body,
      action,
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      target: control,
      source: 'eq-product-card',
      openDrawer: wantsDrawer,
      beforeResolve: applyCart,
    });
    // Horizon's drawer auto-opens for `add` only, so a decrement has to ask, and only once the
    // write has settled — opening sooner would show the drawer still holding the pre-change quantity.
    const drawer = document.querySelector('cart-drawer-component');
    if (cart && wantsDrawer && action !== 'add' && drawer?.hasAttribute('auto-open')) {
      drawer.closest('theme-drawer')?.open();
    }
  } finally {
    delete control.dataset.busy;
  }
};
const watchQuantityControls = () => {
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const control = target.closest(QUANTITY_SELECTOR);
    if (!(control instanceof HTMLElement)) {
      expandStepper(null);
      return;
    }
    const variantId = control.dataset.variantId;
    if (!variantId) return;
    if (isCompact()) expandStepper(target.closest(STEPPER_SELECTOR));
    event.preventDefault();
    const input = control.querySelector('.eq-product-card__quantity-input');
    const current = input instanceof HTMLInputElement ? Number(input.value) || 1 : 1;
    const { add, change } = cartRoutes();
    // `/cart/change.js` addresses a line by its key when there is one, so the same variant added
    // twice with different properties still resolves to the line the card is showing.
    const lineId = control.dataset.lineKey ?? variantId;
    const quantityOffer = Boolean(control.closest('eq-offer-builder[data-offer-mode="quantity-discount"]'));
    // `current` is what the stepper shows, which on a split line is the two lines together. The
    // write addresses one line, so it steps that line's own quantity.
    const currentLineQuantity =
      control.dataset.lineQuantity === undefined ? current : Number(control.dataset.lineQuantity);
    if (target.closest('.eq-product-card__quantity-add')) {
      event.preventDefault();
      const property = control.dataset.lineProperty;
      mutateCart({
        action: 'add',
        url: add,
        body: { id: variantId, quantity: 1, ...(property && { properties: { [property]: 'true' } }) },
        variantId,
        quantity: 1,
        control,
      });
      return;
    }
    // A quantity-discount line may be split into paid/free Shopify cart keys. Never fall back
    // to a bare variant ID or an unset quantity for an already-visible stepper: change.js uses
    // absolute quantities, so either fallback can overwrite the wrong cart line.
    if (quantityOffer && (!control.dataset.lineKey || !Number.isFinite(currentLineQuantity))) {
      void syncFromCart();
      return;
    }
    // The stepper writes an absolute quantity to `/cart/change.js`, but the event it announces is
    // the delta — a step up is an add and a step down a remove, which is what the drawer and the
    // notification key off. The last step down is still a `remove`; only the wording differs.
    if (target.closest('.eq-product-card__quantity-plus')) {
      event.preventDefault();
      mutateCart({
        action: 'add',
        url: change,
        body: { id: lineId, quantity: currentLineQuantity + 1 },
        variantId,
        quantity: 1,
        control,
      });
      return;
    }
    if (target.closest('.eq-product-card__quantity-minus')) {
      mutateCart({
        action: 'remove',
        url: change,
        body: { id: lineId, quantity: Math.max(currentLineQuantity - 1, 0) },
        variantId,
        quantity: 1,
        control,
      });
    }
  });
};
const watchAdds = () => {
  new MutationObserver((records) => {
    for (const record of records) {
      const button = record.target;
      if (!(button instanceof HTMLElement) || button.dataset.added !== 'true') continue;
      button.closest(CARD_SELECTOR)?.classList.add(ACTIVE_CLASS);
    }
  }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['data-added'] });
};
const watchCart = () => {
  const cartIcon = document.querySelector(CART_ICON_SELECTOR);
  if (!cartIcon) return;
  let pending = 0;
  new MutationObserver(() => {
    clearTimeout(pending);
    pending = setTimeout(syncFromCart, 200);
  }).observe(cartIcon, { childList: true, subtree: true, characterData: true });
};
const start = () => {
  watchAdds();
  watchCart();
  watchQuantityControls();
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
window.addEventListener('pageshow', (event) => {
  if (event.persisted) syncFromCart();
});
